import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, TextField, Stack, Tooltip } from '@mui/material';
import axios from '../../../config/axiosInstance';
import { WEEKDAYS } from './staffingUtils';

const pad = (n) => String(n).padStart(2, '0');
const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
};

// Vista de solo lectura: valor final de cada día (por defecto + excepciones) para el área.
// Los días que salen de una regla se resaltan y muestran de dónde viene el valor.
export default function StaffingMonthPreview({ stationId, area, workshifts, refreshKey }) {
  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  const days = useMemo(() => {
    const [year, mon] = month.split('-').map(Number);
    const last = new Date(year, mon, 0).getDate();
    return Array.from({ length: last }, (_, i) => {
      const iso = `${year}-${pad(mon)}-${pad(i + 1)}`;
      const weekday = (new Date(year, mon - 1, i + 1).getDay() + 6) % 7; // 0 = lunes
      return { iso, day: i + 1, weekday };
    });
  }, [month]);

  const load = useCallback(async () => {
    if (!stationId || !days.length) return;
    try {
      const res = await axios.get(`/station/${stationId}/staffing/preview`, {
        params: { from: days[0].iso, to: days[days.length - 1].iso },
      });
      setRows(res.data || []);
      setError(null);
    } catch (e) {
      setRows([]);
      setError('No se pudo cargar la vista previa.');
    }
  }, [stationId, days]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // (turno, fecha) -> { total sumado en el área, ruleIds }
  const cells = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      if (!area.workareaIds.includes(r.workarea_id)) return;
      const key = `${r.workshift_id}|${r.date}`;
      if (!map[key]) map[key] = { total: 0, ruleIds: [] };
      map[key].total += Number(r.total || 0);
      if (r.source !== 'default') map[key].ruleIds.push(r.source);
    });
    return map;
  }, [rows, area]);

  // Los turnos no principales solo se listan si tienen dotación en alguno de los días del mes.
  const visibleWorkshifts = useMemo(
    () => workshifts.filter((w) => w.is_principal || days.some((d) => (cells[`${w.id}|${d.iso}`]?.total || 0) > 0)),
    [workshifts, days, cells]
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Dotación resultante de cada día. Los días resaltados usan una excepción o un valor por día de la semana.
        </Typography>
        <TextField type="month" size="small" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
      </Stack>
      {error && <Typography color="error" variant="body2">{error}</Typography>}
      <Box sx={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
        <Table size="small" sx={{ '& td, & th': { px: 0.5, py: 0.5, textAlign: 'center', minWidth: 30 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ textAlign: 'left !important', minWidth: 140 }}>Turno</TableCell>
              {days.map((d) => (
                <TableCell key={d.iso} sx={{ color: d.weekday >= 5 ? 'error.main' : 'inherit' }}>
                  <div style={{ fontSize: 10 }}>{WEEKDAYS[d.weekday].short}</div>
                  <div>{d.day}</div>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleWorkshifts.map((w) => (
              <TableRow key={w.id}>
                <TableCell sx={{ textAlign: 'left !important', fontWeight: 600 }}>{w.name}</TableCell>
                {days.map((d) => {
                  const cell = cells[`${w.id}|${d.iso}`];
                  const fromRule = cell && cell.ruleIds.length > 0;
                  return (
                    <TableCell
                      key={d.iso}
                      sx={{ bgcolor: fromRule ? '#fff4d6' : 'inherit', fontWeight: fromRule ? 700 : 400 }}
                    >
                      {cell ? (
                        <Tooltip title={fromRule ? 'Definido por una excepción o por el día de la semana' : 'Valor por defecto'}>
                          <span>{cell.total}</span>
                        </Tooltip>
                      ) : '—'}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
